import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Avatar } from '../ui';
import { MAX_SIZES } from '../../constants/defaults';
import { updateProfilePhoto, updateUserProfile, updateBanner, removeBanner, updateProfileBackground, removeProfileBackground } from '../../services/profileService';
import { validateImage, validateImageOrGif } from '../../services/imageService';
import { clearUserCache } from '../../hooks/useUserData';
import { updateAuthorInfoInUserPosts } from '../../services/postService';
import ImageCropModal from './ImageCropModal';

const EditProfileModal = ({ userData, currentUser, onClose }) => {
  const [formData, setFormData] = useState({
    displayName: userData.displayName || '',
    bio: userData.bio || '',
    location: userData.location || '',
    username: userData.username || ''
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropAspect, setCropAspect] = useState(1);
  const [cropMode, setCropMode] = useState('avatar');
  const [cropUploading, setCropUploading] = useState(false);
  const [backgroundUploading, setBackgroundUploading] = useState(false);
  const backgroundInputRef = useRef(null);
  const bannerImageInputRef = useRef(null);
  const bannerGifInputRef = useRef(null);
  const avatarImageInputRef = useRef(null);
  const avatarGifInputRef = useRef(null);
  const defaultPremiumColor = userData.premiumNameColor || '#F59E0B';
  const [premiumNameColor, setPremiumNameColor] = useState(defaultPremiumColor);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleCropComplete = async (blob) => {
    setCropModalOpen(false);
    setCropImageSrc(null);
    const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });
    setCropUploading(true);
    setError('');
    try {
      if (cropMode === 'avatar') {
        const photoResult = await updateProfilePhoto(file, userData.id, currentUser, userData.photoURL);
        if (!photoResult.success) throw new Error(photoResult.error);
        setPreviewUrl(URL.createObjectURL(blob));
        clearUserCache(userData.id);
        try {
          await updateAuthorInfoInUserPosts(userData.id, { profileImage: photoResult.photoURL });
        } catch (e) {
          console.warn('No se pudieron actualizar los posts:', e);
        }
        setSuccess('Foto de perfil actualizada');
      } else {
        const bannerResult = await updateBanner(file, userData.id, userData.bannerURL);
        if (!bannerResult.success) throw new Error(bannerResult.error);
        setBannerPreviewUrl(URL.createObjectURL(blob));
        clearUserCache(userData.id);
        setSuccess('Banner actualizado');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setCropUploading(false);
    }
  };

  const handleRemoveBanner = async () => {
    if (!window.confirm('¿Quitar el banner del perfil?')) return;
    setError('');
    setLoading(true);
    try {
      const result = await removeBanner(userData.id, userData.bannerURL);
      if (!result.success) throw new Error(result.error);
      setBannerPreviewUrl(null);
      clearUserCache(userData.id);
      setSuccess('Banner eliminado');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFile = (mode, file, isGif) => {
    if (!file) return;
    if (isGif) {
      const validation = validateImageOrGif(file, mode === 'banner' ? MAX_SIZES.BANNER : MAX_SIZES.AVATAR);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        return;
      }
      setError('');
      setCropUploading(true);
      (async () => {
        try {
          if (mode === 'avatar') {
            const res = await updateProfilePhoto(file, userData.id, currentUser, userData.photoURL);
            if (!res.success) throw new Error(res.error);
            setPreviewUrl(URL.createObjectURL(file));
            try {
              await updateAuthorInfoInUserPosts(userData.id, { profileImage: res.photoURL });
            } catch (e) {
              console.warn('No se pudieron actualizar los posts:', e);
            }
            setSuccess('Foto de perfil actualizada');
          } else {
            const res = await updateBanner(file, userData.id, userData.bannerURL);
            if (!res.success) throw new Error(res.error);
            setBannerPreviewUrl(URL.createObjectURL(file));
            setSuccess('Banner actualizado');
          }
          clearUserCache(userData.id);
        } catch (err) {
          setError(err.message);
        } finally {
          setCropUploading(false);
        }
      })();
      return;
    }
    const validation = validateImage(file);
    if (!validation.isValid) {
      setError(validation.errors.join(', '));
      return;
    }
    setError('');
    const reader = new FileReader();
    reader.onloadend = () => {
      setCropImageSrc(reader.result);
      setCropAspect(mode === 'avatar' ? 1 : 3);
      setCropMode(mode);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleBackgroundSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !userData?.isPremium) return;
    const type = file.type.startsWith('video/') ? 'video' : (file.type === 'image/gif' ? 'gif' : 'image');
    setError('');
    setBackgroundUploading(true);
    try {
      const res = await updateProfileBackground(type, file, userData.id, userData.profileBackgroundURL);
      if (!res.success) throw new Error(res.error);
      clearUserCache(userData.id);
      setSuccess('Fondo de perfil actualizado');
    } catch (err) {
      setError(err.message);
    } finally {
      setBackgroundUploading(false);
    }
    e.target.value = '';
  };

  const handleRemoveBackground = async () => {
    if (!window.confirm('¿Quitar el fondo del perfil?')) return;
    setError('');
    setBackgroundUploading(true);
    try {
      const res = await removeProfileBackground(userData.id, userData.profileBackgroundURL);
      if (!res.success) throw new Error(res.error);
      clearUserCache(userData.id);
      setSuccess('Fondo eliminado');
    } catch (err) {
      setError(err.message);
    } finally {
      setBackgroundUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Actualizar datos del perfil (foto y banner se actualizan desde el modal de selección/crop)
      const updateData = {
        displayName: formData.displayName,
        bio: formData.bio,
        location: formData.location,
        username: formData.username
      };

      if (userData.isPremium) {
        updateData.premiumNameColor = premiumNameColor || null;
      }

      const profileResult = await updateUserProfile(userData.id, updateData);

      if (!profileResult.success) {
        throw new Error(profileResult.error);
      }

      // Actualizar authorInfo en posts (foto, nombre) para todos los usuarios
      try {
        const authorInfo = {
          displayName: formData.displayName || userData.displayName,
          profileImage: userData.photoURL
        };
        if (userData.isPremium) {
          authorInfo.isPremium = true;
          authorInfo.premiumNameColor = premiumNameColor || null;
          authorInfo.photoURLIsGif = userData.photoURLIsGif;
        }
        await updateAuthorInfoInUserPosts(userData.id, authorInfo);
      } catch (e) {
        console.warn('No se pudieron actualizar los posts:', e);
      }

      clearUserCache(userData.id);
      setSuccess('Perfil actualizado correctamente');
      setLoading(false);
      
      // Cerrar modal y recargar para ver cambios
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div 
          className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-lg w-full p-6 animate-fade-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Editar Perfil
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm text-green-700 dark:text-green-400">{success}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Banner */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Banner del perfil
              </label>
              <div className="relative h-32 rounded-lg overflow-hidden bg-gradient-to-br from-indigo-500 to-purple-600 dark:from-indigo-900 dark:to-purple-900">
                {(bannerPreviewUrl || userData.bannerURL) ? (
                  <img
                    src={bannerPreviewUrl || userData.bannerURL}
                    alt="Banner"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <div className="absolute bottom-2 right-2 flex items-center gap-2">
                  {(bannerPreviewUrl || userData.bannerURL) && (
                    <button
                      type="button"
                      onClick={handleRemoveBanner}
                      disabled={loading}
                      className="px-3 py-1.5 bg-red-500/80 hover:bg-red-600/90 backdrop-blur-sm text-white text-sm rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Eliminar banner
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Recomendado: 1200x400px. Máximo {MAX_SIZES.BANNER / (1024 * 1024)}MB
              </p>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => bannerImageInputRef.current?.click()}
                  disabled={cropUploading}
                  className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-gray-300 dark:border-slate-600 hover:border-indigo-500 dark:hover:border-indigo-500 hover:bg-indigo-50/50 dark:hover:bg-indigo-900/10 transition-colors disabled:opacity-50"
                >
                  <svg className="w-6 h-6 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Subir imagen</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">JPG, PNG o WebP</span>
                </button>
                {userData?.isPremium ? (
                  <button
                    type="button"
                    onClick={() => bannerGifInputRef.current?.click()}
                    disabled={cropUploading}
                    className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-gray-300 dark:border-slate-600 hover:border-indigo-500 dark:hover:border-indigo-500 hover:bg-indigo-50/50 dark:hover:bg-indigo-900/10 transition-colors disabled:opacity-50"
                  >
                    <span className="text-lg font-semibold">GIF</span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Banner animado</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Premium</span>
                  </button>
                ) : (
                  <Link
                    to="/premium"
                    className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-amber-400/60 dark:border-amber-500/60 bg-amber-50/50 dark:bg-amber-900/10 transition-colors"
                  >
                    <span className="text-lg font-semibold">GIF</span>
                    <span className="text-sm font-medium text-amber-800 dark:text-amber-200">Anima con Premium</span>
                    <span className="text-xs text-amber-600 dark:text-amber-400">Obtener Premium</span>
                  </Link>
                )}
              </div>
              <input
                ref={bannerImageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/jpg"
                className="hidden"
                onChange={(e) => handleSelectFile('banner', e.target.files?.[0], false)}
              />
              <input
                ref={bannerGifInputRef}
                type="file"
                accept="image/gif"
                className="hidden"
                onChange={(e) => handleSelectFile('banner', e.target.files?.[0], true)}
              />
            </div>

            {/* Foto de Perfil */}
            <div className="flex flex-col items-center gap-4 pt-4 border-t border-gray-200 dark:border-slate-700">
              <Avatar
                src={previewUrl || userData.photoURL}
                alt={userData.displayName}
                fallbackText={userData.displayName}
                size="3xl"
              />

              <div className="text-center">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => avatarImageInputRef.current?.click()}
                    disabled={cropUploading}
                    className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-gray-300 dark:border-slate-600 hover:border-indigo-500 dark:hover:border-indigo-500 hover:bg-indigo-50/50 dark:hover:bg-indigo-900/10 transition-colors disabled:opacity-50"
                  >
                    <svg className="w-6 h-6 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Subir imagen</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">JPG, PNG o WebP</span>
                  </button>
                  {userData?.isPremium ? (
                    <button
                      type="button"
                      onClick={() => avatarGifInputRef.current?.click()}
                      disabled={cropUploading}
                      className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-gray-300 dark:border-slate-600 hover:border-indigo-500 dark:hover:border-indigo-500 hover:bg-indigo-50/50 dark:hover:bg-indigo-900/10 transition-colors disabled:opacity-50"
                    >
                      <span className="text-lg font-semibold">GIF</span>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Avatar animado</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Premium</span>
                    </button>
                  ) : (
                    <Link
                      to="/premium"
                      className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-amber-400/60 dark:border-amber-500/60 bg-amber-50/50 dark:bg-amber-900/10 transition-colors"
                    >
                      <span className="text-lg font-semibold">GIF</span>
                      <span className="text-sm font-medium text-amber-800 dark:text-amber-200">Anima con Premium</span>
                      <span className="text-xs text-amber-600 dark:text-amber-400">Obtener Premium</span>
                    </Link>
                  )}
                </div>
                <input
                  ref={avatarImageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/jpg"
                  className="hidden"
                  onChange={(e) => handleSelectFile('avatar', e.target.files?.[0], false)}
                />
                <input
                  ref={avatarGifInputRef}
                  type="file"
                  accept="image/gif"
                  className="hidden"
                  onChange={(e) => handleSelectFile('avatar', e.target.files?.[0], true)}
                />
              </div>
            </div>

            {/* Fondo de perfil (Premium) */}
            <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fondo de perfil
              </label>
              {userData?.isPremium ? (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={backgroundInputRef}
                    type="file"
                    accept="image/*,video/mp4,video/webm"
                    className="hidden"
                    onChange={handleBackgroundSelect}
                  />
                  <button
                    type="button"
                    onClick={() => backgroundInputRef.current?.click()}
                    disabled={backgroundUploading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 disabled:opacity-50"
                  >
                    {userData.profileBackgroundURL ? 'Cambiar fondo' : 'Añadir fondo'}
                  </button>
                  {userData.profileBackgroundURL && (
                    <button
                      type="button"
                      onClick={handleRemoveBackground}
                      disabled={backgroundUploading}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800 disabled:opacity-50"
                    >
                      Quitar fondo
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  <Link to="/premium" className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium">
                    Obtener Premium
                  </Link>
                  {' '}para personalizar el fondo de tu perfil (imagen, video o GIF).
                </p>
              )}
            </div>

            {/* Color del nombre (Premium) */}
            {userData?.isPremium && (
              <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Color del nombre
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="color"
                    value={premiumNameColor}
                    onChange={(e) => setPremiumNameColor(e.target.value)}
                    className="w-14 h-10 rounded-lg border border-gray-200 dark:border-slate-600 bg-transparent cursor-pointer"
                  />
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      Hex: <code className="font-mono text-gray-900 dark:text-white">{premiumNameColor}</code>
                    </span>
                    <span className="text-sm font-semibold" style={{ color: premiumNameColor }}>
                      {formData.displayName || userData.displayName || 'Vista previa'}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Este color se usará para mostrar tu nombre en posts, servicios y todo Illustra.
                </p>
              </div>
            )}

            {/* Nombre de usuario */}
            <Input
              label="Nombre de usuario"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="tunombredeusuario"
              disabled={loading}
            />

            {/* Nombre completo */}
            <Input
              label="Nombre"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              placeholder="Tu nombre"
              required
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              }
            />

            {/* Biografía */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Biografía
              </label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                placeholder="Cuéntanos sobre ti..."
                rows={4}
                maxLength={160}
                disabled={loading}
                className="
                  block w-full px-4 py-2.5 rounded-lg
                  bg-white dark:bg-slate-800
                  border-2 border-gray-300 dark:border-slate-600
                  text-gray-900 dark:text-gray-100
                  placeholder-gray-400 dark:placeholder-gray-500
                  transition-all duration-200
                  focus:outline-none focus:ring-2 focus:ring-offset-0
                  focus:border-indigo-500 focus:ring-indigo-500/20
                  resize-none
                "
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                {formData.bio.length}/160
              </p>
            </div>

            {/* Ubicación */}
            <Input
              label="Ubicación"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Ciudad, País"
              disabled={loading}
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              }
            />

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                fullWidth
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="gradient"
                fullWidth
                loading={loading}
                disabled={loading}
              >
                Guardar Cambios
              </Button>
            </div>
          </form>
        </div>
      </div>

      {cropModalOpen && cropImageSrc && (
        <ImageCropModal
          imageSrc={cropImageSrc}
          aspect={cropAspect}
          onComplete={handleCropComplete}
          onCancel={() => { setCropModalOpen(false); setCropImageSrc(null); }}
        />
      )}
    </div>
  );
};

export default EditProfileModal;
